import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Save, Stethoscope, CheckCircle } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import ToastMsg from '../../components/common/ToastMsg';

const SPECIALTIES = [
  'Cardiology', 'Cardiac Surgery', 'Orthopedics', 'Neurology', 'Neurosurgery',
  'Gastroenterology', 'General Surgery', 'Urology', 'Nephrology', 'Oncology',
  'ENT', 'Ophthalmology', 'Gynecology', 'Pediatrics', 'Dermatology', 'Other',
];

const EMPTY = {
  patient_name: '',
  patient_mobile: '',
  patient_email: '',
  department_of_interest: '',
  expected_specialty: '',
  clinical_reason: '',
};


export default function PartnerPortalNewReferral() {
  const navigate = useNavigate();
  const { create: apiCreate } = useApiMethods();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(null);   // { enquiry_id, referral_id }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_name.trim() || !form.patient_mobile.trim() || saving) return;
    if (!/^\+?[0-9\s-]{7,15}$/.test(form.patient_mobile.trim())) {
      setError('Please enter a valid mobile number.');
      return;
    }
    setSaving(true); setError(null);
    try {
      const resp = await apiCreate('/engage/portal/referrals/', form);
      const data = resp?.data || resp?.data?.data;
      if (resp?.status === 'success' && data?.enquiry_id) {
        setSubmitted(data);
        ToastMsg('success', 'Patient referred to the hospital.');
      } else {
        setError(resp?.message || 'Failed to submit referral.');
      }
    } catch (err) {
      const msg = err?.response?.data?.message
        || (err?.response?.data && JSON.stringify(err.response.data))
        || 'Failed to submit referral.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const submitAnother = () => {
    setForm(EMPTY);
    setSubmitted(null);
    setError(null);
  };

  const INPUT = "w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500";
  const L = ({ children }) => <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{children}</label>;

  if (submitted) {
    return (
      <div className="p-6 max-w-xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-8 h-8" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Referral submitted</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            The hospital has received the details for <b>{submitted?.referral?.patient_name || 'this patient'}</b> and will reach out soon.
            You'll see the status update on your dashboard as it moves through the funnel.
          </p>
          <div className="flex items-center justify-center gap-2 mt-5">
            <button onClick={submitAnother} className="px-3 py-1.5 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
              Refer another patient
            </button>
            <Link to="/partner-portal/referrals" className="px-3 py-1.5 text-sm rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-sm hover:from-emerald-600 hover:to-teal-700">
              View my referrals
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 max-w-2xl mx-auto space-y-4">
      <button onClick={() => navigate('/partner-portal/referrals')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-sm">
          <Stethoscope className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Refer a new patient</h1>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Fill in as much as you know — the hospital coordinator will follow up with the patient.</p>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <form onSubmit={submit} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <L>Patient name <span className="text-red-500">*</span></L>
            <input required value={form.patient_name} onChange={e => set('patient_name', e.target.value)} className={INPUT} placeholder="e.g. Ramesh Iyer" />
          </div>
          <div>
            <L>Mobile <span className="text-red-500">*</span></L>
            <input required value={form.patient_mobile} onChange={e => set('patient_mobile', e.target.value)} className={INPUT} placeholder="+91…" />
          </div>
          <div>
            <L>Email</L>
            <input type="email" value={form.patient_email} onChange={e => set('patient_email', e.target.value)} className={INPUT} placeholder="Optional" />
          </div>
          <div>
            <L>Referred specialty</L>
            <select value={form.expected_specialty} onChange={e => set('expected_specialty', e.target.value)} className={INPUT}>
              <option value="">— Select —</option>
              {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <L>Department of interest</L>
            <input value={form.department_of_interest} onChange={e => set('department_of_interest', e.target.value)} className={INPUT} placeholder="e.g. Cardiac care, joint replacement…" />
          </div>
          <div className="md:col-span-2">
            <L>Clinical reason / notes</L>
            <textarea rows={4} value={form.clinical_reason} onChange={e => set('clinical_reason', e.target.value)} className={INPUT}
              placeholder="Diagnosis, presenting complaints, past history, urgency — anything that would help the hospital team." />
            <div className="text-[10px] text-gray-400 mt-1">Max 500 characters. Kept confidential and visible only to the hospital team.</div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
          <button type="button" onClick={() => navigate('/partner-portal/referrals')}
            className="px-4 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button type="submit" disabled={saving || !form.patient_name.trim() || !form.patient_mobile.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm shadow-sm hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Submitting…' : 'Submit referral'}
          </button>
        </div>
      </form>
    </div>
  );
}
